import type { AppStartMode } from '../domain/settings'
import { contextPath, readContextRoute, readContextCollectionRoute, readAdminRoute, readLinksRoute, readNoteRoute } from './noteRoute'

export type AppLaunchSource = 'launch' | 'share' | 'shortcut' | 'widget'

export type AppLaunchIntent =
  | {
      kind: 'capture'
      source: AppLaunchSource
      sharedBody: string
      shareId: string | null
      shareError: boolean
    }
  | { kind: 'search'; source: Exclude<AppLaunchSource, 'share'>; focus: boolean; collection?: true; links?: true }
  | { kind: 'open-context'; source: 'launch'; contextId: string }
  | { kind: 'open-note'; source: 'launch' | 'shortcut' | 'widget'; noteId: string }

interface LaunchParams {
  targetURL: string
}

interface WebLaunchQueue {
  setConsumer(consumer: (parameters: LaunchParams) => void): void
}

declare global {
  interface Window {
    launchQueue?: WebLaunchQueue
  }
}

function recognizedSource(value: string | null): AppLaunchSource | null {
  if (value === 'share' || value === 'shortcut' || value === 'widget') return value
  return null
}

function sharedBody(parameters: URLSearchParams): string {
  return ['title', 'text', 'url']
    .map((key) => parameters.get(key)?.trim() ?? '')
    .filter((value, index, values) => value && values.indexOf(value) === index)
    .join('\n')
}

export function parseAppLaunchIntent(
  targetUrl: string | URL,
  appOrigin = window.location.origin,
  startMode: AppStartMode = 'browse',
): AppLaunchIntent | null {
  const url = targetUrl instanceof URL ? targetUrl : new URL(targetUrl, appOrigin)
  if (url.origin !== appOrigin) return null

  const parameters = url.searchParams
  if (parameters.has('share-target')) {
    return {
      kind: 'capture',
      source: 'share',
      sharedBody: sharedBody(parameters),
      shareId: parameters.get('share-id'),
      shareError: parameters.has('share-error'),
    }
  }

  const routeNoteId = readNoteRoute(url.pathname)
  if (routeNoteId) {
    return {
      kind: 'open-note',
      source: recognizedSource(parameters.get('source')) === 'shortcut' ? 'shortcut' : 'launch',
      noteId: routeNoteId,
    }
  }

  const contextId = readContextRoute(url.pathname)
  if (contextId) return { kind: 'open-context', source: 'launch', contextId }
  if (readContextCollectionRoute(url.pathname)) return { kind: 'search', source: 'launch', focus: false, collection: true }
  if (readLinksRoute(url.pathname)) return { kind: 'search', source: 'launch', focus: false, links: true }

  const requestedSource = recognizedSource(parameters.get('source'))
  const explicitMode = parameters.get('mode')
  if (explicitMode === 'search') {
    return {
      kind: 'search',
      source: requestedSource === 'widget' || requestedSource === 'shortcut'
        ? requestedSource
        : 'launch',
      focus: true,
    }
  }

  if (explicitMode !== 'capture' && startMode === 'browse') {
    return { kind: 'search', source: 'launch', focus: true }
  }

  return {
    kind: 'capture',
    source: requestedSource ?? 'launch',
    sharedBody: '',
    shareId: null,
    shareError: false,
  }
}

export function launchIntentKey(intent: AppLaunchIntent): string {
  if (intent.kind === 'open-context') return `context:${intent.contextId}`
  if (intent.kind === 'open-note') return `note:${intent.noteId}`
  if (intent.kind === 'search') return intent.collection ? 'contexts' : intent.links ? 'links' : `search:${intent.source}`
  if (intent.shareId) return `share:${intent.shareId}`
  return `capture:${intent.source}`
}

export function shouldRestoreActiveDraft(intent: AppLaunchIntent): boolean {
  return intent.kind !== 'capture' || intent.source === 'launch'
}

export function captureEntryIsInstant(intent: AppLaunchIntent): boolean {
  return intent.kind === 'capture'
    && (intent.source === 'widget' || intent.source === 'shortcut')
}

export function subscribeToWebLaunches(
  consumer: (targetUrl: string) => void,
  launchQueue = window.launchQueue,
): () => void {
  if (!launchQueue) return () => undefined
  let active = true
  launchQueue.setConsumer(({ targetURL }) => {
    if (active) consumer(targetURL)
  })
  return () => { active = false }
}

interface AppLaunchDispatcherOptions {
  appOrigin: string
  readStartMode: () => AppStartMode
  duplicateWindowMs: number
  initialIntent: AppLaunchIntent
  accept: (intent: AppLaunchIntent) => Promise<void>
  onAccepted?: (intent: AppLaunchIntent, targetUrl: string) => void
  onError?: (error: unknown) => void
  now?: () => number
}

export interface AppLaunchDispatcher {
  dispatch(targetUrl: string): Promise<void>
}

export interface AppLaunchActions {
  beginNewCapture(): Promise<boolean>
  consumeSharedCapture(intent: Extract<AppLaunchIntent, { kind: 'capture' }>): Promise<void>
  preserveCurrentDraft(): Promise<void>
  dismissTransientSurfaces(): void
  showSearch(focus: boolean, collection?: boolean): void
  showContext(contextId: string): void
  showNote(noteId: string): void
}

export async function applyAppLaunchIntent(
  intent: AppLaunchIntent,
  actions: AppLaunchActions,
): Promise<void> {
  if (intent.kind === 'capture') {
    const started = await actions.beginNewCapture()
    if (!started) throw new Error('A note transition is already in progress.')
    actions.dismissTransientSurfaces()
    if (intent.source === 'share') await actions.consumeSharedCapture(intent)
    return
  }

  await actions.preserveCurrentDraft()
  actions.dismissTransientSurfaces()
  if (intent.kind === 'search') {
    if (intent.collection) actions.showSearch(false, true)
    else actions.showSearch(intent.focus)
    return
  }
  if (intent.kind === 'open-context') { actions.showContext(intent.contextId); return }
  actions.showNote(intent.noteId)
}

export function cleanAppLaunchPath(
  intent: AppLaunchIntent,
  targetUrl: string | URL,
  appOrigin = window.location.origin,
): string {
  const target = new URL(targetUrl, appOrigin)
  if (readAdminRoute(target.pathname)) {
    const safeQuery = new URLSearchParams()
    const record = target.searchParams.get('record')
    const filter = target.searchParams.get('filter')
    if (record && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(record)) safeQuery.set('record', record)
    if (filter && ['needs_review', 'approved', 'deferred', 'declined', 'pending', 'review_due', 'issued', 'accepted', 'expired', 'revoked', 'active', 'all'].includes(filter)) safeQuery.set('filter', filter)
    return target.pathname + (safeQuery.size ? `?${safeQuery}` : '')
  }
  if (target.pathname === '/access/accept') return target.pathname
  if (intent.kind === 'search' && intent.collection) return '/contexts'
  if (intent.kind === 'search' && intent.links) return '/links'
  if (intent.kind === 'open-context') return contextPath(intent.contextId)
  if (intent.kind !== 'open-note') return '/'
  return target.pathname
}

export function createAppLaunchDispatcher({
  appOrigin,
  readStartMode,
  duplicateWindowMs,
  initialIntent,
  accept,
  onAccepted,
  onError,
  now = Date.now,
}: AppLaunchDispatcherOptions): AppLaunchDispatcher {
  let queue = Promise.resolve()
  const handledAt = new Map([[launchIntentKey(initialIntent), now()]])

  return {
    dispatch(targetUrl) {
      queue = queue
        .then(async () => {
          const intent = parseAppLaunchIntent(targetUrl, appOrigin, readStartMode())
          if (!intent) return

          const currentTime = now()
          for (const [key, acceptedAt] of handledAt) {
            if (currentTime - acceptedAt > duplicateWindowMs) handledAt.delete(key)
          }

          const key = launchIntentKey(intent)
          if (handledAt.has(key)) return
          await accept(intent)
          handledAt.set(key, now())
          onAccepted?.(intent, targetUrl)
        })
        .catch((error: unknown) => onError?.(error))
      return queue
    },
  }
}
